import type { DashboardWidgetContent } from '../Components/Dashboard/dashboardTypes';
import {
  isElectronicSensorStreamSensorKey,
  isMotorStreamSensorKey,
  isOtherElectronicsStreamSensorKey,
  motorMultiWidgetDataKey,
  parseMotorMultiWidgetDataKey,
  streamPortFromSensorKey,
} from '../Components/Graphs/sensor-utils';

export function remapStreamPortInSensorKey(sensorKey: string, newPort: number): string {
  return sensorKey.replace(/\.port\d+\./, `.port${newPort}.`);
}

function remapDriveUnitPortInStreamKey(sensorKey: string, oldPort: number, newPort: number): string {
  const parsedMulti = parseMotorMultiWidgetDataKey(sensorKey);
  if (parsedMulti && parsedMulti.port === oldPort) {
    return motorMultiWidgetDataKey(newPort, parsedMulti.prefix);
  }

  const usesPort = isMotorStreamSensorKey(sensorKey) || isElectronicSensorStreamSensorKey(sensorKey) || isOtherElectronicsStreamSensorKey(sensorKey);
  return usesPort && streamPortFromSensorKey(sensorKey) === oldPort ? remapStreamPortInSensorKey(sensorKey, newPort) : sensorKey;
}

function remapSelectedPorts(ports: number[] | undefined, oldPort: number, newPort: number): number[] | undefined {
  if (!ports?.length) return ports;
  return [...new Set(ports.map((port) => (port === oldPort ? newPort : port)))].sort((a, b) => a - b);
}

/** Remaps stream keys and selected drive-unit ports when a motor or sensor port changes. */
export function remapDriveUnitPortInWidgetContent(content: DashboardWidgetContent, oldPort: number, newPort: number): DashboardWidgetContent {
  const dataKey = remapDriveUnitPortInStreamKey(content.dataKey, oldPort, newPort);
  const sensorKeys = (content.sensorKeys ?? []).map((key) => remapDriveUnitPortInStreamKey(key, oldPort, newPort));

  const selectedMotorPorts = remapSelectedPorts(content.options?.selectedMotorPorts, oldPort, newPort);
  const selectedMotorPort = content.options?.selectedMotorPort === oldPort ? newPort : content.options?.selectedMotorPort;
  const selectedSensorPorts = remapSelectedPorts(content.options?.selectedSensorPorts, oldPort, newPort);

  if (
    selectedMotorPorts === content.options?.selectedMotorPorts &&
    selectedMotorPort === content.options?.selectedMotorPort &&
    selectedSensorPorts === content.options?.selectedSensorPorts
  ) {
    return { ...content, dataKey, sensorKeys };
  }

  return {
    ...content,
    dataKey,
    sensorKeys,
    options: {
      ...content.options,
      ...(selectedMotorPorts !== undefined ? { selectedMotorPorts } : {}),
      ...(selectedMotorPort !== undefined ? { selectedMotorPort } : {}),
      ...(selectedSensorPorts !== undefined ? { selectedSensorPorts } : {}),
    },
  };
}
